import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Package, OrganizationSubscription, PREDEFINED_PACKAGES, AVAILABLE_MODULES } from '../types/package';

export class PackageService {
  private packagesCollection = collection(db, 'packages');
  private subscriptionsCollection = collection(db, 'subscriptions');

  // Package CRUD operations
  async getAllPackages(): Promise<Package[]> {
    try {
      const querySnapshot = await getDocs(
        query(this.packagesCollection, orderBy('createdAt', 'desc'))
      );
      
      const packages = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Package[];

      // Ha nincs csomag a rendszerben, inicializáljuk az előre definiáltakkal
      if (packages.length === 0) {
        await this.initializePredefinedPackages();
        return this.getAllPackages(); // Rekurzív hívás az inicializálás után
      }

      return packages;
    } catch (error) {
      console.error('Error fetching packages:', error);
      throw error;
    }
  }

  async getPackageById(id: string): Promise<Package | null> {
    try {
      const docRef = doc(this.packagesCollection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: docSnap.data().createdAt?.toDate() || new Date(),
          updatedAt: docSnap.data().updatedAt?.toDate() || new Date()
        } as Package;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching package:', error);
      throw error;
    }
  }

  async createPackage(packageData: Omit<Package, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(this.packagesCollection, {
        ...packageData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating package:', error);
      throw error;
    }
  }

  async updatePackage(id: string, packageData: Partial<Omit<Package, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    try {
      const docRef = doc(this.packagesCollection, id);
      await updateDoc(docRef, {
        ...packageData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating package:', error);
      throw error;
    }
  }

  async deletePackage(id: string): Promise<void> {
    try {
      // Ellenőrizzük, hogy van-e aktív előfizetés erre a csomagra
      const activeSubscriptions = await this.getSubscriptionsByPackage(id);
      const hasActiveSubscriptions = activeSubscriptions.some(sub => 
        sub.status === 'active' || sub.status === 'trial'
      );

      if (hasActiveSubscriptions) {
        throw new Error('Nem törölhető a csomag, mert vannak aktív előfizetések hozzá rendelve.');
      }

      const docRef = doc(this.packagesCollection, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting package:', error);
      throw error;
    }
  }

  // Subscription CRUD operations
  async getAllSubscriptions(): Promise<OrganizationSubscription[]> {
    try {
      const querySnapshot = await getDocs(
        query(this.subscriptionsCollection, orderBy('createdAt', 'desc'))
      );
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate?.toDate() || new Date(),
        endDate: doc.data().endDate?.toDate(),
        trialEndDate: doc.data().trialEndDate?.toDate(),
        lastPaymentDate: doc.data().lastPaymentDate?.toDate(),
        nextPaymentDate: doc.data().nextPaymentDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        usage: {
          ...doc.data().usage,
          lastUpdated: doc.data().usage?.lastUpdated?.toDate() || new Date()
        }
      })) as OrganizationSubscription[];
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }
  }

  async getSubscriptionsByOrganization(organizationId: string): Promise<OrganizationSubscription[]> {
    try {
      const querySnapshot = await getDocs(
        query(
          this.subscriptionsCollection, 
          where('organizationId', '==', organizationId),
          orderBy('createdAt', 'desc')
        )
      );
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate?.toDate() || new Date(),
        endDate: doc.data().endDate?.toDate(),
        trialEndDate: doc.data().trialEndDate?.toDate(),
        lastPaymentDate: doc.data().lastPaymentDate?.toDate(),
        nextPaymentDate: doc.data().nextPaymentDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        usage: {
          ...doc.data().usage,
          lastUpdated: doc.data().usage?.lastUpdated?.toDate() || new Date()
        }
      })) as OrganizationSubscription[];
    } catch (error) {
      console.error('Error fetching organization subscriptions:', error);
      throw error;
    }
  }

  async getSubscriptionsByPackage(packageId: string): Promise<OrganizationSubscription[]> {
    try {
      const querySnapshot = await getDocs(
        query(
          this.subscriptionsCollection, 
          where('packageId', '==', packageId)
        )
      );
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate?.toDate() || new Date(),
        endDate: doc.data().endDate?.toDate(),
        trialEndDate: doc.data().trialEndDate?.toDate(),
        lastPaymentDate: doc.data().lastPaymentDate?.toDate(),
        nextPaymentDate: doc.data().nextPaymentDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        usage: {
          ...doc.data().usage,
          lastUpdated: doc.data().usage?.lastUpdated?.toDate() || new Date()
        }
      })) as OrganizationSubscription[];
    } catch (error) {
      console.error('Error fetching package subscriptions:', error);
      throw error;
    }
  }

  async createSubscription(subscriptionData: Omit<OrganizationSubscription, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      // Ellenőrizzük, hogy a szervezetnek nincs-e már aktív előfizetése
      const existingSubscriptions = await this.getSubscriptionsByOrganization(subscriptionData.organizationId);
      const hasActiveSubscription = existingSubscriptions.some(sub => 
        sub.status === 'active' || sub.status === 'trial'
      );

      if (hasActiveSubscription) {
        throw new Error('A szervezetnek már van aktív előfizetése.');
      }

      const docRef = await addDoc(this.subscriptionsCollection, {
        ...subscriptionData,
        startDate: Timestamp.fromDate(subscriptionData.startDate),
        endDate: subscriptionData.endDate ? Timestamp.fromDate(subscriptionData.endDate) : null,
        trialEndDate: subscriptionData.trialEndDate ? Timestamp.fromDate(subscriptionData.trialEndDate) : null,
        lastPaymentDate: subscriptionData.lastPaymentDate ? Timestamp.fromDate(subscriptionData.lastPaymentDate) : null,
        nextPaymentDate: subscriptionData.nextPaymentDate ? Timestamp.fromDate(subscriptionData.nextPaymentDate) : null,
        usage: {
          ...subscriptionData.usage,
          lastUpdated: Timestamp.fromDate(subscriptionData.usage.lastUpdated)
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  async updateSubscription(id: string, subscriptionData: Partial<Omit<OrganizationSubscription, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    try {
      const docRef = doc(this.subscriptionsCollection, id);
      
      const updateData: any = {
        ...subscriptionData,
        updatedAt: serverTimestamp()
      };

      // Dátumok konvertálása Timestamp-re
      if (subscriptionData.startDate) {
        updateData.startDate = Timestamp.fromDate(subscriptionData.startDate);
      }
      if (subscriptionData.endDate) {
        updateData.endDate = Timestamp.fromDate(subscriptionData.endDate);
      }
      if (subscriptionData.trialEndDate) {
        updateData.trialEndDate = Timestamp.fromDate(subscriptionData.trialEndDate);
      }
      if (subscriptionData.lastPaymentDate) {
        updateData.lastPaymentDate = Timestamp.fromDate(subscriptionData.lastPaymentDate);
      }
      if (subscriptionData.nextPaymentDate) {
        updateData.nextPaymentDate = Timestamp.fromDate(subscriptionData.nextPaymentDate);
      }
      if (subscriptionData.usage) {
        updateData.usage = {
          ...subscriptionData.usage,
          lastUpdated: Timestamp.fromDate(subscriptionData.usage.lastUpdated || new Date())
        };
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  async deleteSubscription(id: string): Promise<void> {
    try {
      const docRef = doc(this.subscriptionsCollection, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting subscription:', error);
      throw error;
    }
  }

  // Utility methods
  async getAvailableModules() {
    return AVAILABLE_MODULES;
  }

  async getModulesByPackage(packageId: string): Promise<typeof AVAILABLE_MODULES> {
    try {
      const pkg = await this.getPackageById(packageId);
      if (!pkg) {
        throw new Error('Csomag nem található');
      }

      return AVAILABLE_MODULES.filter(module => 
        pkg.modules.includes(module.id) || pkg.customModules?.includes(module.id)
      );
    } catch (error) {
      console.error('Error fetching package modules:', error);
      throw error;
    }
  }

  async checkModuleAccess(organizationId: string, moduleId: string): Promise<boolean> {
    try {
      const subscriptions = await this.getSubscriptionsByOrganization(organizationId);
      const activeSubscription = subscriptions.find(sub => 
        sub.status === 'active' || sub.status === 'trial'
      );

      if (!activeSubscription) {
        return false;
      }

      const pkg = await this.getPackageById(activeSubscription.packageId);
      if (!pkg) {
        return false;
      }

      return pkg.modules.includes(moduleId) || 
             (pkg.customModules?.includes(moduleId) || false);
    } catch (error) {
      console.error('Error checking module access:', error);
      return false;
    }
  }

  async updateUsageStats(subscriptionId: string, usage: Partial<OrganizationSubscription['usage']>): Promise<void> {
    try {
      const docRef = doc(this.subscriptionsCollection, subscriptionId);
      await updateDoc(docRef, {
        usage: {
          ...usage,
          lastUpdated: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating usage stats:', error);
      throw error;
    }
  }

  // Inicializálja az előre definiált csomagokat
  private async initializePredefinedPackages(): Promise<void> {
    try {
      console.log('Initializing predefined packages...');
      
      for (const packageData of PREDEFINED_PACKAGES) {
        await addDoc(this.packagesCollection, {
          ...packageData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      console.log('Predefined packages initialized successfully');
    } catch (error) {
      console.error('Error initializing predefined packages:', error);
      throw error;
    }
  }

  // Statisztikák és jelentések
  async getPackageStats() {
    try {
      const packages = await this.getAllPackages();
      const subscriptions = await this.getAllSubscriptions();

      const stats = {
        totalPackages: packages.length,
        activePackages: packages.filter(pkg => pkg.isActive).length,
        totalSubscriptions: subscriptions.length,
        activeSubscriptions: subscriptions.filter(sub => sub.status === 'active').length,
        trialSubscriptions: subscriptions.filter(sub => sub.status === 'trial').length,
        monthlyRevenue: subscriptions
          .filter(sub => sub.status === 'active')
          .reduce((total, sub) => {
            const pkg = packages.find(p => p.id === sub.packageId);
            return total + (pkg?.price.monthly || 0);
          }, 0),
        packageDistribution: packages.map(pkg => ({
          packageId: pkg.id,
          packageName: pkg.name,
          subscriptionCount: subscriptions.filter(sub => sub.packageId === pkg.id).length,
          activeSubscriptionCount: subscriptions.filter(sub => 
            sub.packageId === pkg.id && (sub.status === 'active' || sub.status === 'trial')
          ).length
        }))
      };

      return stats;
    } catch (error) {
      console.error('Error fetching package stats:', error);
      throw error;
    }
  }
}

export const packageService = new PackageService();
